import React, { useState } from "react";
import { PopupModal, useCalendlyEventListener } from "react-calendly";
import { ChildrenProps } from "../../types";
import { Button } from "../Button/Button";
import type { ButtonSize } from "../Button/Button.types";
import { useWidgetOptions } from "./useWidgetOptions";
import { Prefill } from "react-calendly/typings/calendly";

/**
 * Button's union groups sizes per variant, so a forwarded variant has to be
 * narrowed to one group before it reaches a Button.
 */
type BookCallAppearance =
    | {
          variant: "primary" | "secondary" | "tertiary";
          size?: Exclude<ButtonSize, "xs">;
      }
    | { variant: "link"; size?: "xs" | "sm" };

type BookCallProps = ChildrenProps &
    BookCallAppearance & {
        url: string;
        onBooked?: () => void;
        disabled?: boolean;
        className?: string;
        style?: React.CSSProperties;
        "data-testid"?: string;
        customAnswers?: Prefill["customAnswers"];
    };

export const BookCallButton: React.FC<BookCallProps> = (props) => {
    const { url, children, onBooked, disabled, className, style } = props;
    const { prefill, pageSettings } = useWidgetOptions({
        customAnswers: props.customAnswers,
    });
    const [open, setOpen] = useState(false);

    useCalendlyEventListener({
        onEventScheduled: onBooked,
    });

    const buttonProps = {
        disabled,
        className,
        style,
        "data-testid": props["data-testid"],
        onClick: () => setOpen(true),
        children,
    };

    const renderTrigger = () => {
        if (props.variant === "link") {
            return (
                <Button
                    variant="link"
                    size={props.size ?? "sm"}
                    {...buttonProps}
                />
            );
        }
        if (props.variant === "tertiary") {
            return (
                <Button
                    variant="tertiary"
                    size={props.size ?? "md"}
                    {...buttonProps}
                />
            );
        }
        return (
            <Button
                variant={props.variant}
                size={props.size ?? "md"}
                {...buttonProps}
            />
        );
    };

    return (
        <>
            {renderTrigger()}

            <PopupModal
                onModalClose={() => setOpen(false)}
                open={open}
                rootElement={document.body}
                url={url}
                pageSettings={pageSettings}
                prefill={prefill}
            />
        </>
    );
};
